/**
 * Waben-Reaktionen on this device (amendment 24): asking every few seconds what was thrown
 * around the middle of the map, and handing the map what it should let rise.
 *
 * The question names res-5 regions — the one under the map's middle and the ring around it —
 * and never the map's own box: asked this often, the box would be as good as a position, and
 * the regions say no more than the geofence feed already does. Which of the answers are on
 * screen, the map works out for itself.
 */

import { cellFor, neighbours, REGION_RESOLUTION } from '$domain/h3.ts';
import { request } from './api.ts';

const POLL_MS = 6_000;
/** How long a reaction waits for the map to pick it up before it is too late to rise. */
const KEEP_MS = 15_000;

export type Rising = { id: string; cell: string; emoji: string; at: number };

type Thrown = { id: string; cell: string; emoji: string };

class Reactions {
  /** What the map should let rise, oldest first. The map lets each id rise once. */
  rising = $state<Rising[]>([]);
  #regions: string | undefined;
  #after = '0';
  /** This device's own: they rose the moment they were thrown and need not rise again. */
  #mine = new Set<string>();
  #busy = false;

  /** The map's middle is here now: ask about the regions around it. */
  around(lat: number, lon: number): void {
    this.#regions = neighbours(cellFor(lat, lon, REGION_RESOLUTION)).join(',');
  }

  /** Ask while the page is in view; the returned function stops it. */
  start(): () => void {
    const timer = setInterval(() => void this.#poll(), POLL_MS);
    void this.#poll();
    return () => clearInterval(timer);
  }

  /** Throw one: it rises here at once, and on everybody else's map with their next look. */
  async throwAt(cell: string, emoji: string): Promise<void> {
    const { reaction } = await request<{ reaction: Thrown }>(`/api/cells/${cell}/reactions`, {
      method: 'POST',
      body: JSON.stringify({ emoji }),
    });
    this.#mine.add(reaction.id);
    this.#add([reaction]);
  }

  async #poll(): Promise<void> {
    if (this.#busy || this.#regions === undefined) return;
    if (document.visibilityState !== 'visible') return;
    this.#busy = true;
    try {
      const { reactions } = await request<{ reactions: Thrown[] }>(
        `/api/reactions?cell5=${this.#regions}&after=${this.#after}`,
      );
      const last = reactions.at(-1);
      if (last !== undefined) this.#after = last.id;
      this.#add(reactions.filter((entry) => !this.#mine.has(entry.id)));
    } catch {
      // Offline, or between sessions: nothing rises, and the next look asks again.
    } finally {
      this.#busy = false;
    }
  }

  #add(entries: Thrown[]): void {
    const now = Date.now();
    const waiting = this.rising.filter((entry) => now - entry.at < KEEP_MS);
    if (entries.length === 0 && waiting.length === this.rising.length) return;
    this.rising = [...waiting, ...entries.map((entry) => ({ ...entry, at: now }))];
  }
}

export const reactions = new Reactions();
